import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useSettingsStore } from './settings'
import { getFontCatalog, registerCatalogFamily } from '../services/fonts/fontCatalog'
import { readInstalledFamilies, readOfflinePinnedFamilies, writeInstalledFamily } from '../services/fonts/fontCatalogStorage'
import type { FontCatalogFamily } from '../types/fontCatalog'
import { APP_FONTS, APP_FONT_CAPABILITIES, type FontCapabilities } from '../constants/fonts'

const FALLBACK_FONTS = [
  'Georgia',
  'Garamond',
  'Times New Roman',
  'Palatino',
  'Inter',
  'Roboto',
  'Helvetica Neue',
  'Arial',
  'Merriweather',
  'Lora',
  'Playfair Display',
  'Fira Code',
  'JetBrains Mono',
  'Monospace',
]

function sortFamilies(families: FontCatalogFamily[]): FontCatalogFamily[] {
  return [...families].sort((a, b) => a.family.localeCompare(b.family))
}

// Synced font IDs the catalog no longer has. Startup skips them, so a removed
// font doesn't load the catalog on every launch; Settings > Fonts still checks.
const ABSENT_IDS_KEY = 'fontCatalog.absentIds'

function readAbsentIds(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(ABSENT_IDS_KEY) || '[]')
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [])
  } catch {
    return new Set()
  }
}

function writeAbsentIds(ids: readonly string[]): void {
  try {
    if (ids.length) localStorage.setItem(ABSENT_IDS_KEY, JSON.stringify(ids))
    else localStorage.removeItem(ABSENT_IDS_KEY)
  } catch {
    // Without storage, startup just checks the catalog again next time.
  }
}

export const useFontsStore = defineStore('fonts', () => {
  const settings = useSettingsStore()
  const systemFonts = ref<string[]>([])
  const systemFontCapabilities = ref<Record<string, FontCapabilities>>({})
  const loaded = ref(false)
  const loading = ref(false)
  const catalogFamilies = ref<FontCatalogFamily[]>([])
  const installedCatalogFamilies = ref<FontCatalogFamily[]>([])
  const catalogLoading = ref(false)
  const catalogLoaded = ref(false)
  const catalogError = ref<string | null>(null)
  const downloadErrors = ref<Record<string, string>>({})
  const downloadingIds = ref<Set<string>>(new Set())
  const readyCatalogIds = ref<Set<string>>(new Set())

  let inFlightPromise: Promise<string[]> | null = null
  let catalogPromise: Promise<FontCatalogFamily[]> | null = null
  let initializationPromise: Promise<void> | null = null
  let settingsWatcherInstalled = false

  const installedCatalogFontNames = computed(() =>
    installedCatalogFamilies.value
      .filter((family) => readyCatalogIds.value.has(family.id))
      .map((family) => family.family),
  )

  /**
   * Fonts on the synced list that aren't ready on this device yet: downloading,
   * or failed. Pickers list them so they don't seem to have vanished.
   */
  const pendingSyncedFonts = computed(() => {
    const hidden = new Set(settings.hiddenAppFonts.map((font) => font.toLocaleLowerCase()))
    const byId = new Map(catalogFamilies.value.map((family) => [family.id, family]))
    return settings.installedFontIds.flatMap((id) => {
      const family = byId.get(id)
      if (!family || readyCatalogIds.value.has(id) || hidden.has(family.family.toLocaleLowerCase())) return []
      return [{ family, downloading: downloadingIds.value.has(id), error: downloadErrors.value[id] ?? null }]
    })
  })

  /** True when synced fonts are missing here and the catalog needed to fetch them can't be reached. */
  const syncedFontsUnreachable = computed(() =>
    !catalogLoaded.value
    && !catalogLoading.value
    && !!catalogError.value
    && settings.installedFontIds.some((id) => !readyCatalogIds.value.has(id)),
  )

  const allAppFontNames = computed(() => {
    const seen = new Set<string>()
    return [...APP_FONTS, ...installedCatalogFontNames.value].filter((font) => {
      const key = font.toLocaleLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  })

  const pickerAppFontNames = computed(() => {
    const availableByName = new Map(
      allAppFontNames.value.map((font) => [font.toLocaleLowerCase(), font]),
    )
    const hidden = new Set(settings.hiddenAppFonts.map((font) => font.toLocaleLowerCase()))
    const result: string[] = []
    const added = new Set<string>()

    for (const preferredName of settings.appFontOrder) {
      const key = preferredName.toLocaleLowerCase()
      const availableName = availableByName.get(key)
      if (availableName && !hidden.has(key) && !added.has(key)) {
        result.push(availableName)
        added.add(key)
      }
    }

    for (const font of allAppFontNames.value) {
      const key = font.toLocaleLowerCase()
      if (!hidden.has(key) && !added.has(key)) {
        result.push(font)
        added.add(key)
      }
    }

    return result
  })

  function isAppFontVisible(font: string): boolean {
    const normalized = font.toLocaleLowerCase()
    return pickerAppFontNames.value.some((name) => name.toLocaleLowerCase() === normalized)
  }

  /**
   * A picker order built from this device's fonts, with fonts that aren't
   * ready here yet kept where they were. The order syncs, so dropping them
   * would lose them on every other device too.
   */
  function withUnavailableKept(available: readonly string[]): string[] {
    const ready = new Set(allAppFontNames.value.map((font) => font.toLocaleLowerCase()))
    const queue = [...available]
    const result: string[] = []
    for (const name of settings.appFontOrder) {
      if (!ready.has(name.toLocaleLowerCase())) result.push(name)
      else if (queue.length) result.push(queue.shift()!)
    }
    return [...result, ...queue]
  }

  function setAppFontOrder(fonts: readonly string[]): void {
    const availableByName = new Map(
      allAppFontNames.value.map((font) => [font.toLocaleLowerCase(), font]),
    )
    const ordered: string[] = []
    const seen = new Set<string>()
    for (const font of fonts) {
      const key = font.toLocaleLowerCase()
      const availableName = availableByName.get(key)
      if (availableName && !seen.has(key)) {
        ordered.push(availableName)
        seen.add(key)
      }
    }
    settings.appFontOrder = withUnavailableKept(ordered)
  }

  function hideAppFont(font: string): void {
    const normalized = font.toLocaleLowerCase()
    if (!settings.hiddenAppFonts.some((name) => name.toLocaleLowerCase() === normalized)) {
      settings.hiddenAppFonts = [...settings.hiddenAppFonts, font]
    }
    settings.appFontOrder = settings.appFontOrder.filter(
      (name) => name.toLocaleLowerCase() !== normalized,
    )
  }

  function showAppFont(font: string): void {
    const normalized = font.toLocaleLowerCase()
    settings.hiddenAppFonts = settings.hiddenAppFonts.filter(
      (name) => name.toLocaleLowerCase() !== normalized,
    )
    if (!settings.appFontOrder.some((name) => name.toLocaleLowerCase() === normalized)) {
      settings.appFontOrder = withUnavailableKept([
        ...pickerAppFontNames.value.filter((name) => name.toLocaleLowerCase() !== normalized),
        font,
      ])
    }
  }

  async function loadFonts(): Promise<string[]> {
    if (loaded.value) return systemFonts.value
    if (loading.value && inFlightPromise) return inFlightPromise

    loading.value = true
    inFlightPromise = (async () => {
      try {
        const result = await window.api?.listFonts()
        if (Array.isArray(result) && result.length > 0) {
          systemFonts.value = result.map((font) => font.family)
          systemFontCapabilities.value = Object.fromEntries(
            result.map((font) => [font.family.toLocaleLowerCase(), {
              bold: font.bold,
              italic: font.italic,
              weights: font.weights,
              variableWeightRange: font.variableWeightRange,
            }]),
          )
        } else if (systemFonts.value.length === 0) {
          systemFonts.value = FALLBACK_FONTS
        }
      } catch (err) {
        console.warn('[FontsStore] Failed to load system fonts:', err)
        if (systemFonts.value.length === 0) systemFonts.value = FALLBACK_FONTS
      } finally {
        loaded.value = true
        loading.value = false
        inFlightPromise = null
      }
      return systemFonts.value
    })()

    return inFlightPromise
  }

  async function rehydrateStoredFamilies(ids: readonly string[]): Promise<void> {
    const pinned = await readOfflinePinnedFamilies()
    const stored = sortFamilies(await readInstalledFamilies([...new Set([...ids, ...pinned.map(family => family.id)])]))
    installedCatalogFamilies.value = stored

    await Promise.all(stored.map(async (family) => {
      try {
        await registerCatalogFamily(family, { fetchMissingCore: true })
        readyCatalogIds.value = new Set(readyCatalogIds.value).add(family.id)
        const { [family.id]: _cleared, ...remaining } = downloadErrors.value
        downloadErrors.value = remaining
      } catch (error) {
        const nextReady = new Set(readyCatalogIds.value)
        nextReady.delete(family.id)
        readyCatalogIds.value = nextReady
        downloadErrors.value = {
          ...downloadErrors.value,
          [family.id]: error instanceof Error ? error.message : String(error),
        }
        console.warn(`[FontsStore] Failed to rehydrate ${family.family}:`, error)
      }
    }))
  }

  /**
   * A device that synced fonts from another has only their IDs. Load the
   * catalog to look up their files and download them in the background.
   */
  async function fetchMissingSyncedFonts(): Promise<void> {
    const absent = readAbsentIds()
    const missing = settings.installedFontIds.some((id) => !readyCatalogIds.value.has(id) && !absent.has(id))
    if (!missing) return
    if (catalogLoaded.value) {
      reconcileSyncedFamilies(catalogFamilies.value)
      return
    }
    await loadCatalog().catch((error) => {
      console.warn('[FontsStore] Could not load the font catalog for synced fonts:', error)
    })
  }

  async function initializeCatalogFonts(): Promise<void> {
    if (initializationPromise) return initializationPromise
    initializationPromise = (async () => {
      await rehydrateStoredFamilies(settings.installedFontIds)
      void fetchMissingSyncedFonts()

      if (!settingsWatcherInstalled) {
        settingsWatcherInstalled = true
        watch(
          () => [...settings.installedFontIds],
          async (ids) => {
            await rehydrateStoredFamilies(ids)
            await fetchMissingSyncedFonts()
          },
        )
      }
    })()
    return initializationPromise
  }

  /** Tries the synced fonts again after the catalog or a download failed. */
  async function retrySyncedFonts(): Promise<void> {
    if (catalogLoaded.value) reconcileSyncedFamilies(catalogFamilies.value)
    else await loadCatalog().catch(() => {})
  }

  /** Downloads one synced font that isn't ready here, e.g. when picked from a list. */
  async function downloadSyncedFont(family: FontCatalogFamily): Promise<void> {
    await installCatalogFamily(family, { updateSyncedIds: false, makeVisible: false })
  }

  async function installCatalogFamily(
    family: FontCatalogFamily,
    options: { updateSyncedIds?: boolean; makeVisible?: boolean } = {},
  ): Promise<void> {
    if (downloadingIds.value.has(family.id)) return
    downloadingIds.value = new Set(downloadingIds.value).add(family.id)
    const { [family.id]: _cleared, ...remainingErrors } = downloadErrors.value
    downloadErrors.value = remainingErrors

    try {
      await registerCatalogFamily(family, { fetchMissingCore: true })
      await writeInstalledFamily(family)
      readyCatalogIds.value = new Set(readyCatalogIds.value).add(family.id)
      installedCatalogFamilies.value = sortFamilies([
        ...installedCatalogFamilies.value.filter((item) => item.id !== family.id),
        family,
      ])

      if (options.updateSyncedIds !== false) {
      if (options.makeVisible !== false) showAppFont(family.family)
        if (!settings.installedFontIds.includes(family.id)) {
          settings.installedFontIds = [...settings.installedFontIds, family.id].sort()
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      downloadErrors.value = { ...downloadErrors.value, [family.id]: message }
      throw error
    } finally {
      const next = new Set(downloadingIds.value)
      next.delete(family.id)
      downloadingIds.value = next
    }
  }

  function reconcileSyncedFamilies(families: FontCatalogFamily[]): void {
    const desired = new Set(settings.installedFontIds)
    const listed = new Set(families.map((family) => family.id))
    writeAbsentIds([...desired].filter((id) => !listed.has(id)))

    // A newly synced device has only IDs. Once the user opens the catalog and
    // its manifest is available, independently fetch that device's core files.
    for (const family of families) {
      if (desired.has(family.id) && !readyCatalogIds.value.has(family.id)) {
        void installCatalogFamily(family, { updateSyncedIds: false, makeVisible: false }).catch((error) => {
          console.warn(`[FontsStore] Failed to install synced font ${family.family}:`, error)
        })
      }
    }
  }

  async function loadCatalog(): Promise<FontCatalogFamily[]> {
    if (catalogLoaded.value) return catalogFamilies.value
    if (catalogPromise) return catalogPromise

    catalogLoading.value = true
    catalogError.value = null
    catalogPromise = (async () => {
      try {
        // TODO(font-catalog pipeline): this first-pass list only needs family,
        // id, category, license and subsets. Publish a lightweight index plus
        // per-family detail files upstream to avoid transferring full `files`
        // arrays for discovery.
        // TODO(previews): either register a non-persisted core face for an
        // ephemeral preview, or consume pipeline-generated specimen images.
        const result = await getFontCatalog()
        catalogFamilies.value = sortFamilies(result.manifest.fonts)
        catalogLoaded.value = true
        reconcileSyncedFamilies(catalogFamilies.value)

        if (result.refresh) {
          void result.refresh.then((fresh) => {
            catalogFamilies.value = sortFamilies(fresh.fonts)
            reconcileSyncedFamilies(catalogFamilies.value)
          }).catch((error) => {
            // The cached catalog remains usable offline.
            console.warn('[FontsStore] Font catalog refresh failed:', error)
          })
        }
        return catalogFamilies.value
      } catch (error) {
        catalogError.value = error instanceof Error ? error.message : String(error)
        throw error
      } finally {
        catalogLoading.value = false
        catalogPromise = null
      }
    })()
    return catalogPromise
  }

  function isCatalogFontInstalled(id: string): boolean {
    return readyCatalogIds.value.has(id)
  }

  function findCatalogFamilyByName(name: string): FontCatalogFamily | undefined {
    const normalized = name.replace(/['"]/g, '').split(',')[0].trim().toLocaleLowerCase()
    return catalogFamilies.value.find((family) => family.family.toLocaleLowerCase() === normalized)
  }

  function getFontCapabilities(name: string): FontCapabilities | null {
    const normalized = name.replace(/['"]/g, '').split(',')[0].trim().toLocaleLowerCase()
    if (!normalized) return null

    const catalogFamily = installedCatalogFamilies.value.find(
      (family) => family.family.toLocaleLowerCase() === normalized,
    )
    if (catalogFamily) {
      const staticWeights = new Set<number>()
      const ranges: Array<{ min: number; max: number }> = []
      for (const file of catalogFamily.files) {
        const weights = file.weight.trim().split(/\s+/).map(Number).filter(Number.isFinite)
        if (weights.length === 2) ranges.push({ min: weights[0], max: weights[1] })
        else if (weights.length === 1) staticWeights.add(weights[0])
      }
      const variableWeightRange = ranges.length
        ? {
            min: Math.min(...ranges.map((range) => range.min)),
            max: Math.max(...ranges.map((range) => range.max)),
          }
        : null
      return {
        bold: (variableWeightRange?.max ?? 0) >= 600 || [...staticWeights].some((weight) => weight >= 600),
        italic: catalogFamily.files.some((file) => file.style === 'italic'),
        weights: [...staticWeights].sort((a, b) => a - b),
        variableWeightRange,
      }
    }

    const appEntry = Object.entries(APP_FONT_CAPABILITIES).find(
      ([family]) => family.toLocaleLowerCase() === normalized,
    )
    return appEntry?.[1] ?? systemFontCapabilities.value[normalized] ?? null
  }

  return {
    systemFonts,
    systemFontCapabilities,
    loaded,
    loading,
    catalogFamilies,
    installedCatalogFamilies,
    installedCatalogFontNames,
    allAppFontNames,
    pickerAppFontNames,
    pendingSyncedFonts,
    syncedFontsUnreachable,
    catalogLoading,
    catalogLoaded,
    catalogError,
    downloadErrors,
    downloadingIds,
    readyCatalogIds,
    loadFonts,
    loadCatalog,
    initializeCatalogFonts,
    installCatalogFamily,
    retrySyncedFonts,
    downloadSyncedFont,
    isCatalogFontInstalled,
    isAppFontVisible,
    setAppFontOrder,
    hideAppFont,
    showAppFont,
    findCatalogFamilyByName,
    getFontCapabilities,
  }
})
